import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '../store';

interface ServiceUser {
  id: string;
  email: string;
  nickname: string;
  name?: string;
  service_tier: string;
  has_premium_access: boolean;
}

interface UnifiedAuthContextType {
  // Platform authentication
  isPlatformAuthenticated: boolean;
  isPlatformPilot: boolean;
  isPlatformClubManager: boolean;
  isPlatformAdmin: boolean;
  platformUser: any;
  
  // Service authentication
  isServiceAuthenticated: boolean;
  serviceUser: ServiceUser | null;
  
  // Combined status
  isAuthenticated: boolean;
  userType: 'platform_admin' | 'club_manager' | 'pilot' | 'guest' | null;
  displayName: string;
  email: string;
  
  // Methods
  refreshServiceAuth: () => void;
}

const UnifiedAuthContext = createContext<UnifiedAuthContextType | undefined>(undefined);

export const useUnifiedAuth = () => {
  const context = useContext(UnifiedAuthContext);
  if (!context) {
    throw new Error('useUnifiedAuth must be used within UnifiedAuthProvider');
  }
  return context;
};

interface UnifiedAuthProviderProps {
  children: ReactNode;
}

export const UnifiedAuthProvider: React.FC<UnifiedAuthProviderProps> = ({ children }) => {
  const [serviceUser, setServiceUser] = useState<ServiceUser | null>(null);
  
  // Platform auth from Redux
  const platformUser = useSelector((state: RootState) => state.auth.user);
  const isPlatformAuthenticated = useSelector((state: RootState) => state.auth.isAuthenticated);
  
  // Derive platform roles
  const isPlatformPilot = platformUser?.is_pilot || false;
  const isPlatformClubManager = platformUser?.user_type === 'club_manager';
  const isPlatformAdmin = platformUser?.is_platform_admin || false;
  
  // Check service auth from localStorage
  const refreshServiceAuth = () => {
    const serviceToken = localStorage.getItem('serviceToken');
    const serviceUserData = localStorage.getItem('serviceUserData');
    
    if (serviceToken && serviceUserData) {
      try {
        const userData = JSON.parse(serviceUserData);
        setServiceUser(userData);
      } catch (error) {
        console.error('Failed to parse service user data:', error);
        setServiceUser(null);
      }
    } else {
      setServiceUser(null);
    }
  };
  
  useEffect(() => {
    refreshServiceAuth();
    
    // Listen for storage changes (for when service auth changes in another tab)
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'serviceToken' || e.key === 'serviceUserData') {
        refreshServiceAuth();
      }
    };
    
    // Listen for custom auth change events (for changes in the same tab)
    const handleAuthChange = () => {
      refreshServiceAuth();
    };
    
    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('serviceAuthChanged', handleAuthChange);
    
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('serviceAuthChanged', handleAuthChange);
    };
  }, []);
  
  // Also refresh when platform auth changes
  useEffect(() => {
    refreshServiceAuth();
  }, [isPlatformAuthenticated]);
  
  // Compute combined authentication status
  const isServiceAuthenticated = !!serviceUser;
  const isAuthenticated = isPlatformAuthenticated || isServiceAuthenticated;
  
  // Determine user type with clear hierarchy:
  // 1. Platform admin (highest priority - can have dual role as pilot)
  // 2. Club manager (can have dual role as pilot)
  // 3. Pilot (platform pilot or service premium user)
  // 4. Guest (service free tier user only)
  let userType: UnifiedAuthContextType['userType'] = null;
  if (isPlatformAdmin) {
    userType = 'platform_admin';
  } else if (isPlatformClubManager) {
    userType = 'club_manager';
  } else if (isPlatformPilot) {
    userType = 'pilot';
  } else if (isServiceAuthenticated) {
    // Service-only users (no platform account)
    if (serviceUser?.service_tier === 'premium' || serviceUser?.has_premium_access) {
      userType = 'pilot';
    } else {
      userType = 'guest';
    }
  }
  
  // Get display name (prefer platform user name)
  const displayName = platformUser?.name || 
                      platformUser?.username || 
                      serviceUser?.name || 
                      serviceUser?.nickname || 
                      '';
  
  // Get email (prefer platform user email)
  const email = platformUser?.email || serviceUser?.email || '';
  
  const value: UnifiedAuthContextType = {
    // Platform
    isPlatformAuthenticated,
    isPlatformPilot,
    isPlatformClubManager,
    isPlatformAdmin,
    platformUser,
    
    // Service
    isServiceAuthenticated,
    serviceUser,
    
    // Combined
    isAuthenticated,
    userType,
    displayName,
    email,
    
    // Methods
    refreshServiceAuth,
  };
  
  return (
    <UnifiedAuthContext.Provider value={value}>
      {children}
    </UnifiedAuthContext.Provider>
  );
};